'use client'

import React, { useCallback, useMemo, useRef, useState } from 'react'
import { PageFrame } from './ui'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { AlertCircle, RefreshCw } from 'lucide-react'
import Turnstile, { type TurnstileHandle } from './Turnstile'
import type { ProcessedTheme } from './ProcessedEntry/DomainEntry/processedTypes'
import { StatTile, ChartTooltip } from './charts'
import { ResizableTable, TableTd } from './ResizableTable'
import { parseDbTimestamp } from '@/lib/dates'

const TURNSTILE_SITEKEY = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_TF_CENTER_GETENTRIES_KEY ?? ''

// ─── Types ────────────────────────────────────────────────────────────────────

type Entry = {
  id: string
  createdAt: string
  headline: string | null
  title: string | null
  isScam: boolean
  postType: string | null
  userEntryDomainId?: string | null
  originalUrl?: string | null
  lastMobileIsAlive?: boolean | null
  lastDesktopIsAlive?: boolean | null
  manualInactive?: boolean
}

// ─── Per-platform cache (survives view switches, cleared after 10 min) ────────
const _cache = new Map<string, { entries: Entry[]; at: number }>()
const _inflight = new Map<string, Promise<Entry[]>>()
const CACHE_TTL = 10 * 60 * 1000

// ─── Helpers ──────────────────────────────────────────────────────────────────

function entryLabel(e: Entry): string {
  return e.headline ?? e.title ?? e.postType ?? '—'
}

type Sev = 'critical' | 'warning' | 'info'

function severity(e: Entry): Sev {
  const alive = e.lastMobileIsAlive === true || e.lastDesktopIsAlive === true
  if (e.isScam && alive) return 'critical'
  if (e.isScam) return 'warning'
  return 'info'
}

function domainStatus(e: Entry): string {
  if (e.manualInactive) return 'Manual off'
  if (e.lastMobileIsAlive === true || e.lastDesktopIsAlive === true) return 'Active'
  if (e.lastMobileIsAlive === false || e.lastDesktopIsAlive === false) return 'Down'
  return 'Unknown'
}

function relativeTime(iso: string): string {
  const parsed = parseDbTimestamp(iso)
  if (!parsed) return '—'
  const diff = Date.now() - parsed.getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'Just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return d === 1 ? 'Yesterday' : `${d}d ago`
}

function daysActive(iso: string): string {
  const parsed = parseDbTimestamp(iso)
  if (!parsed) return '—'
  const d = Math.floor((Date.now() - parsed.getTime()) / 86400000)
  if (d < 1) return '< 1D'
  return `${d}D`
}

// Build last-30-days chart data from entries
function buildChartData(entries: Entry[]) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dayMs = 86400000

  const map = new Map<string, { warning: number; info: number }>()
  for (let i = 0; i < 30; i++) {
    const d = new Date(today.getTime() - (29 - i) * dayMs)
    const key = `${d.getDate()}/${d.getMonth() + 1}`
    map.set(key, { warning: 0, info: 0 })
  }

  for (const e of entries) {
    const d = parseDbTimestamp(e.createdAt)
    if (!d) continue
    const key = `${d.getDate()}/${d.getMonth() + 1}`
    const bucket = map.get(key)
    const sev = severity(e)
    if (bucket) bucket[sev === 'critical' ? 'warning' : sev]++
  }

  return Array.from(map.entries()).map(([date, counts]) => ({ date, ...counts }))
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function urgencyClass(label: string, isDark: boolean): string {
  const map: Record<string, string> = {
    '< 1D': 'bg-danger-surface border-destructive/40 text-destructive',
    '1D':   'bg-warning-surface border-warning-border text-warning',
    '2D':   'bg-warning-surface border-warning-border text-warning',
  }
  return map[label] ?? ('bg-secondary border-border text-muted-foreground')
}


// ─── Main component ───────────────────────────────────────────────────────────

export function OverviewView({ brand, platformCode, theme = 'dark' }: { brand: string; platformCode: string; theme?: ProcessedTheme }) {
  const isDark = theme === 'dark'
  const cached = _cache.get(platformCode)
  const hasFreshCache = cached != null && Date.now() - cached.at < CACHE_TTL

  const [entries, setEntries] = useState<Entry[]>(hasFreshCache ? cached.entries : [])
  const [loading, setLoading] = useState(!hasFreshCache)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const turnstileRef = useRef<TurnstileHandle>(null)

  const handleVerify = useCallback(async (token: string) => {
    setError(null)
    try {
      let request = _inflight.get(platformCode)
      if (!request) {
        request = fetch('/api/campaigns/processed', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ turnstileToken: token, platformCode }),
        })
          .then(async (res) => {
            const payload = await res.json()
            if (!payload?.success) {
              throw new Error(payload?.reason ?? 'Failed to load data')
            }
            return (payload.data.entries ?? []) as Entry[]
          })
          .finally(() => {
            _inflight.delete(platformCode)
          })
        _inflight.set(platformCode, request)
      }

      const loaded = await request
      _cache.set(platformCode, { entries: loaded, at: Date.now() })
      setEntries(loaded)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [platformCode])

  const handleTurnstileError = useCallback(() => {
    setLoading(false)
    setRefreshing(false)
    setError('Verification failed — try refreshing')
  }, [])

  const handleRefresh = useCallback(() => {
    setRefreshing(true)
    setError(null)
    _cache.delete(platformCode)
    turnstileRef.current?.reset()
    turnstileRef.current?.run()
  }, [platformCode])

  const domainEntries = useMemo(
    () => entries.filter(e => e.userEntryDomainId != null),
    [entries],
  )

  const recentDetections = useMemo(
    () => [...domainEntries].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 6),
    [domainEntries],
  )

  const activeThreats = useMemo(
    () =>
      domainEntries
        .filter(e => !e.manualInactive && (e.lastMobileIsAlive === true || e.lastDesktopIsAlive === true))
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .slice(0, 4),
    [domainEntries],
  )

  const chartData = useMemo(() => buildChartData(entries), [entries])

  const totals = useMemo(() => ({
    warning:   domainEntries.filter(e => severity(e) === 'critical' || severity(e) === 'warning').length,
    active:    domainEntries.filter(e => domainStatus(e) === 'Active').length,
    takenDown: domainEntries.filter(e => domainStatus(e) === 'Down' || domainStatus(e) === 'Manual off').length,
  }), [domainEntries])

  const border  = 'border-border'
  const panelBg = 'bg-card'
  const divider = 'divide-border'
  const rowLine = 'border-border'
  const label   = 'text-muted-foreground'
  const muted   = 'text-muted-foreground'
  const text    = 'text-foreground'
  const soft    = 'text-muted-foreground'
  const faint   = 'text-foreground'
  const gapBg   = 'bg-muted'
  const softBg  = 'bg-secondary'

  return (
    <PageFrame padded={false} eyebrow={`${brand} · Overview`} title="Threat volume" subtitle="Detections by severity, last 30 days"
      loading={loading} error={error} onRetry={handleRefresh}
      headerActions={
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh"
          className={`flex h-8 w-8 shrink-0 items-center justify-center border transition-colors disabled:opacity-50 hover:text-signal hover:border-signal ${border} ${muted}`}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      }>
      <Turnstile ref={turnstileRef} sitekey={TURNSTILE_SITEKEY} onVerify={handleVerify} onError={handleTurnstileError} onTimeout={handleTurnstileError} autoRun={loading} />
      <div className={`flex flex-col gap-3 p-4 ${softBg}`}>

        {/* ── Stat tiles ───────────────────────────────────────────────── */}
        <div className="grid grid-cols-3 gap-2">
              {([
                { label: 'Scam',     value: totals.warning,   tone: 'amber' as const },
                { label: 'Active',   value: totals.active,    tone: 'sky'   as const },
                { label: 'Inactive', value: totals.takenDown, tone: 'green' as const },
              ] as const).map(({ label: tLabel, value, tone }) => (
                <div
                  key={tLabel}
                  className={`border border-border bg-card overflow-hidden`}
                >
                  <StatTile label={tLabel} value={String(value).padStart(2, '0')} tone={tone} isDark={isDark} />
                </div>
              ))}
        </div>

        {/* ── Main chart ───────────────────────────────────────────────── */}
        <div className={`border border-border bg-card overflow-hidden`}>
              <div className="flex items-center gap-5 px-4 pt-3 pb-1">
                <LegendDot color="bg-warning" label="Scam" isDark={isDark} />
                <LegendDot color="bg-signal"   label="Info" isDark={isDark} />
              </div>
              <ResponsiveContainer width="100%" height={160}>
                <AreaChart data={chartData} margin={{ top: 4, right: 0, left: -24, bottom: 0 }}>
                  <defs>
                    <linearGradient id={`gI-${platformCode}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="var(--signal)" stopOpacity={0.18} />
                      <stop offset="95%" stopColor="var(--signal)" stopOpacity={0.01} />
                    </linearGradient>
                    <linearGradient id={`gW-${platformCode}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#fbbf24" stopOpacity={0.22} />
                      <stop offset="95%" stopColor="#fbbf24" stopOpacity={0.01} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fill: 'var(--subtle-text)', fontSize: 9, fontFamily: 'monospace' }} tickLine={false} axisLine={false} interval={4} />
                  <YAxis tick={{ fill: 'var(--subtle-text)', fontSize: 9, fontFamily: 'monospace' }} tickLine={false} axisLine={false} />
                  <Tooltip content={(props: any) => <ChartTooltip {...props} isDark={isDark} labelMap={{ info: 'Info', warning: 'Scam' }} />} />
                  <Area type="monotone" dataKey="info"    stroke="var(--signal)" strokeWidth={1.5} fill={`url(#gI-${platformCode})`} dot={false} />
                  <Area type="monotone" dataKey="warning" stroke="#fbbf24" strokeWidth={1.5} fill={`url(#gW-${platformCode})`} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
        </div>

        {/* ── Bottom panels ────────────────────────────────────────────── */}
        <div className="grid grid-cols-[1fr_340px] gap-3">

          {/* Recent detections */}
          <div className={`border border-border bg-card overflow-hidden`}>
            <div className={`border-b px-6 py-3 ${border}`}>
              <span className={`font-mono text-[10px] uppercase tracking-widest ${label}`}>Recent detections</span>
            </div>
            {recentDetections.length === 0 ? (
              <p className={`px-6 py-8 font-mono text-[11px] uppercase tracking-widest ${faint}`}>No domain entries found</p>
            ) : (
              <ResizableTable
                columns={[
                  { key: 'domain', label: 'Domain' },
                  { key: 'category', label: 'Category' },
                  { key: 'status', label: 'Status' },
                  { key: 'detected', label: 'Detected' },
                ]}
                maximizable={false}
                theme={isDark ? 'dark' : 'light'}
                thPadding="px-4 py-2"
                thRowClassName={`font-mono text-[10px] uppercase tracking-widest ${muted}`}
              >
                {recentDetections.map(e => (
                  <tr key={e.id} className={`border-b border-l-2 border-l-transparent transition-colors hover:border-l-signal hover:bg-secondary ${rowLine}`}>
                    <TableTd>
                      {e.originalUrl ? (
                        <>
                          <div className={`font-medium truncate max-w-[200px] ${text}`}>{e.originalUrl}</div>
                          <div className={`text-[11px] truncate max-w-[200px] ${muted}`}>{entryLabel(e)}</div>
                        </>
                      ) : (
                        <div className={`font-medium truncate max-w-[200px] ${text}`}>{entryLabel(e)}</div>
                      )}
                    </TableTd>
                    <TableTd className={label}>{e.postType ?? '—'}</TableTd>
                    <TableTd className={soft}>{domainStatus(e)}</TableTd>
                    <TableTd className={`font-mono text-[11px] ${muted}`}>{relativeTime(e.createdAt)}</TableTd>
                  </tr>
                ))}
              </ResizableTable>
            )}
          </div>

          {/* Active threats */}
          <div className={`border border-border bg-card overflow-hidden`}>
            <div className={`border-b px-5 py-3 ${border}`}>
              <span className={`font-mono text-[10px] uppercase tracking-widest ${label}`}>Active threats</span>
            </div>
            {activeThreats.length === 0 ? (
              <p className={`px-5 py-8 font-mono text-[11px] uppercase tracking-widest ${faint}`}>No active domains</p>
            ) : (
              <div className={`divide-y ${divider}`}>
                {activeThreats.map(e => {
                  const age = daysActive(e.createdAt)
                  return (
                    <div key={e.id} className="flex items-start justify-between px-5 py-3.5 transition-colors hover:bg-secondary">
                      <div className="min-w-0">
                        <div className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>{e.postType ?? 'entry'}</div>
                        {e.originalUrl ? (
                          <>
                            <div className={`mt-0.5 truncate text-[13px] font-medium ${text}`}>{e.originalUrl}</div>
                            <div className={`mt-0.5 truncate text-[11px] ${label}`}>{entryLabel(e)}</div>
                          </>
                        ) : (
                          <div className={`mt-0.5 truncate text-[13px] font-medium ${text}`}>{entryLabel(e)}</div>
                        )}
                        <div className={`mt-1 font-mono text-[10px] ${muted}`}>{relativeTime(e.createdAt)}</div>
                      </div>
                      <span className={`ml-3 mt-0.5 shrink-0 border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${urgencyClass(age, isDark)}`}>
                        {age} ACTIVE
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </PageFrame>
  )
}

function LegendDot({ color, label, isDark }: { color: string; label: string; isDark: boolean }) {
  return (
    <span className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-subtle`}>
      <span className={`h-2 w-2 rounded-full ${color}`} />
      {label}
    </span>
  )
}
